import React, { useEffect, useState } from 'react';
import Slideshow from '../components/Slideshow';
import StripesBackground from '../components/StripesBackground';
import { contentAPI } from '../utils/api';

const Home = () => {
  const [content, setContent] = useState(null);
  const [slideshowImages, setSlideshowImages] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to get the correct URL (for Cloudinary or local files)
  const getMediaUrl = (url) => {
    if (!url) return null;
    // If URL starts with http:// or https://, it's already a complete URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise, it's a relative path, prepend BACKEND_URL
    return `${BACKEND_URL}${url}`;
  };

  useEffect(() => {
    loadContent();
    loadSlideshowImages();
  }, []);

  const loadContent = async () => {
    try {
      const response = await contentAPI.getPageContent('home');
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load home content:', error);
    }
  };

  const loadSlideshowImages = async () => {
    try {
      const response = await contentAPI.getSlideshowImages();
      // Handle both API response formats
      const imagesList = response.data.slideshow_images || response.data.images || [];
      const images = imagesList.map(img => {
        // If image starts with http, use as-is, otherwise prepend BACKEND_URL
        return img.startsWith('http') ? img : `${BACKEND_URL}${img}`;
      });
      setSlideshowImages(images);
    } catch (error) {
      console.error('Failed to load slideshow images:', error);
      // Fallback to default images
      setSlideshowImages([
        'https://www.dominiquefiore.com/tests/montreal/preview/homebg/00_HB2_03.jpg',
        'https://www.dominiquefiore.com/tests/montreal/preview/homebg/00_HB2_01.jpg',
        'https://www.dominiquefiore.com/tests/montreal/preview/homebg/02_ET2_07.jpg',
      ]);
    }
  };

  const defaultContent = `Dominique graduated from the I.A.D. Cinema school (Belgium) in 2002. He started his career as a vfx compositor straight ahead by working on films and video content for live spectaculars (Celine Dion in Las Vegas, etc..) In 2007 he moved to London to work on more challenging project (Harry Potter...) with world's top companies (Double Negative,...). 
2011 sees Dominique coming back to Belgium to start working as a supervisor.  
Dominique has more than 13 years experience in the industry and 4 as a supervisor. 
He is also an amateur photographer, graphic designer and musician. 
Have a nice visit!`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="home-page">
      {/* Hero Section with Slideshow */}
      <div className="relative min-h-screen flex items-center">
        <Slideshow images={slideshowImages} />
        <StripesBackground dark className="fixed inset-0 z-10 pointer-events-none" />
        
        <div className="container mx-auto px-6 py-20 relative z-20 flex items-center justify-center min-h-screen" style={{ position: 'relative' }}>
          <div className="w-full max-w-6xl">
            {/* Title Section */}
            <div className="mb-8">
              <h1 className="text-6xl font-light mb-2 font-oswald tracking-wider" data-testid="hero-title">
                <span className="font-light">Dominique Fiore</span>
              </h1>
              <h2 className="text-4xl font-bold font-oswald tracking-wide" data-testid="hero-subtitle">VFX Supervisor</h2>
            </div>

            {/* Text Box and Picture Block - Aligned at top */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16 w-full">
              {/* Left: Text Box */}
              <div className="order-2 lg:order-1">
                <div className="bg-white bg-opacity-5 p-6 rounded-lg backdrop-blur-sm">
                  <h5 className="text-xl font-semibold mb-4 text-white font-oswald">You have a vision.</h5>
                  <div 
                    className="text-gray-300 leading-relaxed font-ubuntu prose prose-invert prose-sm max-w-none"
                    data-testid="hero-description"
                    dangerouslySetInnerHTML={{ __html: content?.content || defaultContent }}
                  />
                </div>
              </div>

              {/* Right: Profile Image */}
              <div className="flex justify-center lg:justify-end order-1 lg:order-2">
                <div className="w-full max-w-sm overflow-hidden border-4 border-white border-opacity-20 shadow-2xl">
                  {content?.profile_image ? (
                    <img
                      src={getMediaUrl(content.profile_image)}
                      alt="Dominique Fiore"
                      className="w-full h-auto object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gray-700 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Profile Photo</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Demoreel Section - Aligned with text and picture block */}
            {content?.demoreel_url && (
              <div className="mt-8 w-full">
                <h3 className="text-2xl font-light mb-6 text-center font-oswald">Demoreel</h3>
                <div className="bg-black bg-opacity-50 p-4 rounded-lg w-full">
                  <video
                    key={content.demoreel_url}
                    controls
                    className="w-full rounded"
                    data-testid="demoreel-video"
                    preload="metadata"
                    playsInline
                  >
                    <source src={getMediaUrl(content.demoreel_url)} type="video/quicktime" />
                    <source src={getMediaUrl(content.demoreel_url)} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
